/**
 * Der **evcc-Standort** (HM-165): das ganze Haus, wie evcc es regelt — die
 * Grenzen, ab denen der Hausakku für Ladepunkte entladen werden darf, ob er aus
 * dem Netz lädt, die Einspeisegrenze, die Tarife.
 *
 * **Ein Knoten wie ein Ladepunkt, aber ohne Zähler.** Das Gateway meldet ihn
 * als `{quelle}.site`, und nur, wenn der Nutzer ihn dort gewählt hat. Er hat
 * keine Rolle, keine Kanäle und keinen Energiestand: Aus ihm entsteht nie ein
 * Anlagevorschlag (HM-058), keine Erzeugungsanlage und kein Steckplatz. Netz,
 * PV und Hausverbrauch sendet das Gateway hier ausdrücklich **nicht** (OQ-049)
 * — die misst der Zähler, nicht evcc.
 *
 * Gedeutet wird wie bei der Wallbox in der API (`evccSiteStateOf`), nicht in
 * der Oberfläche; die Schlüssel sind ein Vertrag mit dem Gateway, und was
 * anders heißt, wird angezeigt und nicht gedeutet (Regel 9).
 */
import type { Unit } from "../common/units.js";
import { isWritable, type DatapointAccess } from "./datapoint.js";

/** Der Gerätetyp: Er nennt die Quelle, wie `evcc_loadpoint` (ADR 0032, Nachtrag). */
export const EVCC_SITE_DEVICE_TYPE = "evcc_site";

export function isEvccSiteDeviceType(deviceType: string): boolean {
  return deviceType === EVCC_SITE_DEVICE_TYPE;
}

/**
 * Die Betriebsart des Hausakkus, wie evcc sie zählt (`api.BatteryMode`).
 * `0` heißt beim Lesen „unbekannt" — evcc weiß es nicht.
 */
export const EVCC_BATTERY_MODES = [
  "unknown",
  "normal",
  "hold",
  "charge",
  "hold_charge",
  "discharge_grid",
] as const;
export type EvccBatteryMode = (typeof EVCC_BATTERY_MODES)[number];
export const EVCC_BATTERY_MODE_CODES: Readonly<Record<EvccBatteryMode, number>> = {
  unknown: 0,
  normal: 1,
  hold: 2,
  charge: 3,
  hold_charge: 4,
  discharge_grid: 5,
};

export function evccBatteryModeOf(code: number): EvccBatteryMode | null {
  return EVCC_BATTERY_MODES.find((mode) => EVCC_BATTERY_MODE_CODES[mode] === code) ?? null;
}

/**
 * Die **Vorgabe** der Betriebsart (`battery-mode-external`). Dieselbe Zählung,
 * aber `0` heißt hier „keine Vorgabe — evcc entscheidet" und nicht
 * „unbekannt". Deshalb ein eigenes Wort für die Null.
 */
export const EVCC_BATTERY_MODE_PRESETS = [
  "none",
  "normal",
  "hold",
  "charge",
  "hold_charge",
  "discharge_grid",
] as const;
export type EvccBatteryModePreset = (typeof EVCC_BATTERY_MODE_PRESETS)[number];
export const EVCC_BATTERY_MODE_PRESET_CODES: Readonly<Record<EvccBatteryModePreset, number>> = {
  none: 0,
  normal: 1,
  hold: 2,
  charge: 3,
  hold_charge: 4,
  discharge_grid: 5,
};

export interface EvccSiteDatapointSpec {
  readonly key: string;
  readonly name: string;
  readonly unit: Unit | null;
  readonly access: DatapointAccess;
}

const spec = (
  key: string,
  name: string,
  unit: Unit | null,
  access: DatapointAccess,
): EvccSiteDatapointSpec => ({ key, name, unit, access });

/**
 * Der Vertrag mit dem Gateway. **`battery-mode-external` ist der erste
 * Datenpunkt eines Gateways mit `access: "write"`**: evcc trägt die Vorgabe in
 * `/api/state` nicht verlässlich, also gibt es nichts zu lesen. Die Oberfläche
 * zeigt ihn als Auswahl ohne „aktuellen Wert" — was gilt, sagt `battery-mode`.
 */
export const EVCC_SITE_DATAPOINTS = {
  bufferSoc: spec("buffer-soc", "Hausakku für Ladepunkte ab", "ratio", "read_write"),
  bufferStartSoc: spec("buffer-start-soc", "Hausakku startet Laden ab", "ratio", "read_write"),
  prioritySoc: spec("priority-soc", "Hausakku hat Vorrang bis", "ratio", "read_write"),
  residualPower: spec("residual-power", "Regelreserve", "W", "read_write"),
  batteryDischargeControl: spec(
    "battery-discharge-control",
    "Hausakku-Entladung beim Laden sperren",
    null,
    "read_write",
  ),
  batteryMode: spec("battery-mode", "Betriebsart des Hausakkus", null, "read"),
  batteryModeExternal: spec(
    "battery-mode-external",
    "Betriebsart des Hausakkus vorgeben",
    null,
    "write",
  ),
  batteryGridChargeLimit: spec(
    "battery-grid-charge-limit",
    "Hausakku aus dem Netz laden unter",
    "EUR/kWh",
    "read_write",
  ),
  batteryGridChargeLimitSet: spec(
    "battery-grid-charge-limit-set",
    "Netzladen des Hausakkus eingerichtet",
    null,
    "read_write",
  ),
  batteryGridChargeActive: spec(
    "battery-grid-charge-active",
    "Hausakku lädt aus dem Netz",
    null,
    "read",
  ),
  batteryGridDischarge: spec(
    "battery-grid-discharge",
    "Hausakku darf ins Netz entladen",
    null,
    "read_write",
  ),
  batteryGridDischargeLimit: spec(
    "battery-grid-discharge-limit",
    "Hausakku ins Netz entladen über",
    "EUR/kWh",
    "read_write",
  ),
  batteryGridDischargeLimitSet: spec(
    "battery-grid-discharge-limit-set",
    "Netzentladen des Hausakkus eingerichtet",
    null,
    "read_write",
  ),
  batteryGridDischargeActive: spec(
    "battery-grid-discharge-active",
    "Hausakku entlädt ins Netz",
    null,
    "read",
  ),
  gridExportLimit: spec("grid-export-limit", "Einspeisegrenze", "W", "read_write"),
  tariffGrid: spec("tariff-grid", "Netzpreis jetzt", "EUR/kWh", "read"),
  tariffFeedIn: spec("tariff-feed-in", "Einspeisevergütung jetzt", "EUR/kWh", "read"),
  tariffCo2: spec("tariff-co2", "CO₂ im Netz jetzt", "g/kWh", "read"),
} as const satisfies Readonly<Record<string, EvccSiteDatapointSpec>>;

export type EvccSiteDatapointName = keyof typeof EVCC_SITE_DATAPOINTS;

export interface EvccSiteField<T> {
  readonly value: T | null;
  readonly datapointId: string | null;
  readonly writable: boolean;
}

export interface EvccSiteState {
  /** Anteile 0…1. */
  readonly bufferSoc: EvccSiteField<number>;
  readonly bufferStartSoc: EvccSiteField<number>;
  readonly prioritySoc: EvccSiteField<number>;
  readonly residualPowerW: EvccSiteField<number>;
  readonly batteryDischargeControl: EvccSiteField<boolean>;
  readonly batteryMode: EvccSiteField<EvccBatteryMode>;
  /** Nur schreibbar — `value` ist immer `null`. */
  readonly batteryModeExternal: EvccSiteField<EvccBatteryModePreset>;
  /** In €/kWh; `null`, solange keine Grenze eingerichtet ist. */
  readonly batteryGridChargeLimitEur: EvccSiteField<number>;
  readonly batteryGridChargeLimitSet: EvccSiteField<boolean>;
  readonly batteryGridChargeActive: EvccSiteField<boolean>;
  readonly batteryGridDischarge: EvccSiteField<boolean>;
  readonly batteryGridDischargeLimitEur: EvccSiteField<number>;
  readonly batteryGridDischargeLimitSet: EvccSiteField<boolean>;
  readonly batteryGridDischargeActive: EvccSiteField<boolean>;
  readonly gridExportLimitW: EvccSiteField<number>;
  readonly tariffGridEurPerKwh: EvccSiteField<number>;
  readonly tariffFeedInEurPerKwh: EvccSiteField<number>;
  readonly tariffCo2GPerKwh: EvccSiteField<number>;
  readonly measuredAt: Date | null;
}

export interface EvccSiteDatapointSource {
  readonly id: string;
  readonly key: string;
  readonly access: DatapointAccess;
  readonly valueType: "number" | "string";
  readonly unit?: Unit | null;
}

export interface EvccSiteValueSource {
  readonly datapointId: string;
  readonly value: number;
  readonly measuredAt: Date;
}

const EMPTY: EvccSiteField<never> = { value: null, datapointId: null, writable: false };
const plain = (raw: number): number => raw;
const flag = (raw: number): boolean | null => (raw === 0 ? false : raw === 1 ? true : null);

/**
 * Der Zustand eines Standorts aus Katalog und letzten Werten — dieselbe
 * Bauweise wie `wallboxStateOf`. Ein Preis gilt nur in der verabredeten
 * Einheit; eine Grenze nur, wenn ihr Schalter `…-set` an ist.
 */
export function evccSiteStateOf(
  datapoints: readonly EvccSiteDatapointSource[],
  values: readonly EvccSiteValueSource[],
): EvccSiteState {
  const byKey = new Map(datapoints.map((point) => [point.key, point]));
  const valueById = new Map(values.map((value) => [value.datapointId, value]));
  let measuredAt: Date | null = null;

  const field = <T>(
    name: EvccSiteDatapointName,
    decode: (raw: number) => T | null,
    unit?: Unit,
  ): EvccSiteField<T> => {
    const point = byKey.get(EVCC_SITE_DATAPOINTS[name].key);
    if (point === undefined || point.valueType !== "number") return EMPTY;
    if (unit !== undefined && point.unit !== unit) return EMPTY;
    const sample = valueById.get(point.id);
    if (sample !== undefined && (measuredAt === null || sample.measuredAt > measuredAt)) {
      measuredAt = sample.measuredAt;
    }
    return {
      value: sample === undefined ? null : decode(sample.value),
      datapointId: point.id,
      writable: isWritable(point.access),
    };
  };
  const limit = (
    name: EvccSiteDatapointName,
    set: EvccSiteField<boolean>,
  ): EvccSiteField<number> => {
    const result = field(name, plain, "EUR/kWh");
    return set.value === true ? result : { ...result, value: null };
  };

  const chargeSet = field("batteryGridChargeLimitSet", flag);
  const dischargeSet = field("batteryGridDischargeLimitSet", flag);
  const state: Omit<EvccSiteState, "measuredAt"> = {
    bufferSoc: field("bufferSoc", plain),
    bufferStartSoc: field("bufferStartSoc", plain),
    prioritySoc: field("prioritySoc", plain),
    residualPowerW: field("residualPower", plain),
    batteryDischargeControl: field("batteryDischargeControl", flag),
    batteryMode: field("batteryMode", evccBatteryModeOf),
    /* Nur schreibbar: Was hier ankäme, wäre nicht die Vorgabe. */
    batteryModeExternal: { ...field("batteryModeExternal", () => null), value: null },
    batteryGridChargeLimitEur: limit("batteryGridChargeLimit", chargeSet),
    batteryGridChargeLimitSet: chargeSet,
    batteryGridChargeActive: field("batteryGridChargeActive", flag),
    batteryGridDischarge: field("batteryGridDischarge", flag),
    batteryGridDischargeLimitEur: limit("batteryGridDischargeLimit", dischargeSet),
    batteryGridDischargeLimitSet: dischargeSet,
    batteryGridDischargeActive: field("batteryGridDischargeActive", flag),
    gridExportLimitW: field("gridExportLimit", plain),
    tariffGridEurPerKwh: field("tariffGrid", plain, "EUR/kWh"),
    tariffFeedInEurPerKwh: field("tariffFeedIn", plain, "EUR/kWh"),
    tariffCo2GPerKwh: field("tariffCo2", plain, "g/kWh"),
  };
  return { ...state, measuredAt };
}

const RATIO = (value: number) =>
  value >= 0 && value <= 1 ? null : "Der Wert ist ein Anteil zwischen 0 und 1.";
const SWITCH = (value: number) =>
  value === 0 || value === 1 ? null : "Der Schalter ist 0 (aus) oder 1 (an).";
const UNSET_ONLY = (value: number) =>
  value === 0
    ? null
    : "Eingerichtet wird eine Grenze mit ihrem Wert; hier lässt sie sich nur mit 0 entfernen.";
const PRICE_LIMIT = (value: number) =>
  value >= -10_000 && value <= 10_000
    ? null
    : "Die Grenze muss zwischen −10 000 und 10 000 €/kWh liegen.";

const WRITE_CHECKS: Readonly<Record<string, (value: number) => string | null>> = {
  [EVCC_SITE_DATAPOINTS.bufferSoc.key]: RATIO,
  [EVCC_SITE_DATAPOINTS.bufferStartSoc.key]: RATIO,
  [EVCC_SITE_DATAPOINTS.prioritySoc.key]: RATIO,
  [EVCC_SITE_DATAPOINTS.residualPower.key]: (value) =>
    value >= -100_000 && value <= 100_000
      ? null
      : "Die Regelreserve muss zwischen −100 000 und 100 000 W liegen.",
  [EVCC_SITE_DATAPOINTS.batteryDischargeControl.key]: SWITCH,
  [EVCC_SITE_DATAPOINTS.batteryModeExternal.key]: (value) =>
    EVCC_BATTERY_MODE_PRESETS.some((preset) => EVCC_BATTERY_MODE_PRESET_CODES[preset] === value)
      ? null
      : "Die Vorgabe ist 0 (keine) oder eine Betriebsart von 1 bis 5.",
  [EVCC_SITE_DATAPOINTS.batteryGridChargeLimit.key]: PRICE_LIMIT,
  [EVCC_SITE_DATAPOINTS.batteryGridChargeLimitSet.key]: UNSET_ONLY,
  [EVCC_SITE_DATAPOINTS.batteryGridDischarge.key]: SWITCH,
  [EVCC_SITE_DATAPOINTS.batteryGridDischargeLimit.key]: PRICE_LIMIT,
  [EVCC_SITE_DATAPOINTS.batteryGridDischargeLimitSet.key]: UNSET_ONLY,
  [EVCC_SITE_DATAPOINTS.gridExportLimit.key]: (value) =>
    value >= 0 && value <= 100_000
      ? null
      : "Die Einspeisegrenze muss zwischen 0 und 100 000 W liegen.",
};

/**
 * Die Prüfung vor dem Senden — dieselben Wertebereiche wie im Gateway. Kein
 * Schutz; der liegt beim Gateway und bei evcc. Für einen fremden Schlüssel gibt
 * es keine Regel und damit keine Ablehnung.
 */
export function evccSiteWriteProblem(key: string, value: number): string | null {
  return WRITE_CHECKS[key]?.(value) ?? null;
}
